import { SelectOptionType } from '@octano/global-ui';
import {
  CreateCourseBody,
  UpdateCourseBody,
} from '../../../types/courseMaintainerTypes';
import { getSelectedOption } from '../../../utils/selectFormat';
import {
  Course,
  CourseFormData,
  CourseFormFields,
  CourseSyllabus,
  RetakeAllowedCodes,
  CourseFormMode,
} from './types';

export const syllabusToFile = (
  syllabus?: CourseSyllabus | null,
): File | null => {
  if (!syllabus) {
    return null;
  }
  return new File([] as any, `${syllabus?.name}.${syllabus?.extension}`, {
    lastModified: -2,
  });
};

export const baseToSelectOption = <T extends { id: number; name: string }>(
  items?: T[],
): SelectOptionType[] =>
  items?.map((e) => ({
    label: e.name,
    value: e.id,
  })) || [];

export const courseToFormFields = (
  course: Course,
  {
    schools = [],
    academicLevels = [],
    modalities = [],
    replaceCourseGradeOptions = [],
  }: CourseFormData,
): CourseFormFields => {
  return {
    // CourseDetailsSection
    shortening: course.shortening,
    code: course.code,
    name: course.name,
    credits: course.credits.toString(),
    totalHours: course.totalHours?.toString() ?? '',
    school: getSelectedOption(course.schoolId, schools),
    academicLevel: getSelectedOption(course.academicLevelId, academicLevels),
    modality: getSelectedOption(course.modalityId ?? 0, modalities),
    replaceCourseGradeAllowed: getSelectedOption(
      course.replaceCourseGradeAllowed
        ? RetakeAllowedCodes.Enabled
        : RetakeAllowedCodes.Disabled,
      replaceCourseGradeOptions,
    ),
    replaceEvaluationGradeAllowed: getSelectedOption(
      course.replaceEvaluationGradeAllowed
        ? RetakeAllowedCodes.Enabled
        : RetakeAllowedCodes.Disabled,
      replaceCourseGradeOptions,
    ),
    // CourseTypeSection
    courseTypes: course?.types?.map((e) => e.id) ?? [],
    // CourseActivityTypeSection,
    attendance:
      course?.assistanceRequirements?.map((e) => ({
        minPercentage: e.minPercentage,
        activityTypeId: e.activityTypeId,
      })) ?? [],
    // CourseSyllabusSection
    syllabusFile: syllabusToFile(course?.syllabus),
    // CourseCompatibilitiesSection
    compatibilities:
      course?.compatibilities?.map((e) => ({
        id: e.id,
        shortening: e.shortening,
        name: e.name,
        credits: e.credits,
      })) ?? [],
    // CoursePrerequisitesSection
    prerequisites:
      course?.dependsOn?.map((e) => ({
        id: e.id,
        shortening: e.shortening,
        name: e.name,
        credits: e.credits,
      })) ?? [],
    learningResults:
      course.courseLearningResults?.map((e, index) => ({
        id: e.id,
        code: e.code,
        description: e.description,
      })) ?? [],
    learningUnits:
      course.courseLearningUnits?.map((e, index) => ({
        id: e.id,
        title: e.title,
        hours: e.hours,
      })) ?? [],
  };
};

export const submitRequiresConfirmation = (
  values: CourseFormFields,
  mode: CourseFormMode,
): boolean => {
  return (
    !values.prerequisites ||
    (values.prerequisites.length === 0 && mode === CourseFormMode.CREATE)
  );
};

export const creationPayload = (values: CourseFormFields): CreateCourseBody => ({
  name: values.name,
  code: values.code,
  shortening: values.shortening,
  credits: +values.credits,
  totalHours: values.totalHours ? +values.totalHours : null,
  types: values?.courseTypes ?? [],
  modalityId: values.modality?.value ? +values.modality?.value : undefined,
  schoolId: values.school?.value ? +values.school?.value : undefined,
  academicLevelId: values.academicLevel?.value
    ? +values.academicLevel?.value
    : undefined,
  assistanceRequirements: values?.attendance ?? [],
  dependsOn:
    values?.prerequisites?.map((prerequisite) => prerequisite.id) ?? [],
  compatibilityIds:
    values?.compatibilities?.map((compatibility) => compatibility.id) ?? [],
  replaceCourseGradeAllowed:
    values.replaceCourseGradeAllowed?.value === RetakeAllowedCodes.Enabled,
  learningResults: values.learningResults.map((lr) => ({
    code: lr.code!,
    description: lr.description!,
  })),
});

export const updatingPayload = (
  courseId: number,
  values: CourseFormFields,
  isDisabled: boolean,
): UpdateCourseBody => {
  const payload: UpdateCourseBody = {
    id: courseId,
    types: values.courseTypes ?? [],
    assistanceRequirements: values?.attendance ?? [],
    dependsOn:
      values?.prerequisites?.map((prerequisite) => prerequisite.id) ?? [],
    compatibilityIds:
      values?.compatibilities?.map((compatibility) => compatibility.id) ?? [],
    modalityId: values.modality?.value ? +values.modality?.value : undefined,
    totalHours: values.totalHours ? +values.totalHours : null,
    replaceCourseGradeAllowed:
      values.replaceCourseGradeAllowed?.value === RetakeAllowedCodes.Enabled,
    replaceEvaluationGradeAllowed:
      values.replaceEvaluationGradeAllowed?.value ===
      RetakeAllowedCodes.Enabled,
    learningResults:
      values.learningResults?.map((lr) => ({
        id: lr.id!,
        code: lr.code!,
        description: lr.description!,
      })) ?? [],
    learningUnits:
      values.learningUnits?.map((lr) => ({
        id: lr.id!,
        title: lr.title!,
        hours: lr.hours!,
      })) ?? [],
  };
  if (!isDisabled) {
    return {
      ...payload,
      name: values.name,
      code: values.code,
      shortening: values.shortening,
      credits: +values.credits,
      schoolId: values.school?.value ? +values.school?.value : undefined,
      academicLevelId: values.academicLevel?.value
        ? +values.academicLevel?.value
        : undefined,
    };
  }
  return payload;
};
